import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { theme } from "@pepsico/layout";
import { updateParamValue, usePagePrefs } from "@pepsico/pref-permissions";
import { Layer, Source, useMap, type MapMouseEvent } from "react-map-gl/mapbox";
import { useLocalStorage } from "usehooks-ts";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import { useGetAllLocations } from "api/locations/locations";
import type { GrowerGeoResponse, LocationResponse } from "api/model";
import {
  LABEL_PLAQUE_IMAGE_IDS,
  labelPlaqueLayout,
} from "components/PlotMap/utils/labelPlaque";
import {
  LOCATION_CLUSTER_MAX_ZOOM,
  LOCATION_CLUSTER_RADIUS,
  LOCATION_PIN_IMAGE_ID,
  addLocationPinImage,
} from "components/PlotMap/utils/locationPin";
import { FIT_BOUNDS_OPTIONS } from "components/PlotMap/utils/mapCameraUtils";
import {
  EMPTY_FEATURE_COLLECTION,
  LABEL_FILL_COLOR,
  SECTOR_BOUNDS,
} from "components/PlotMap/utils/mapConstants";
import { getMeanBoundaryCenter } from "context/PlotMap/boundaryUtils";
import { getBoundsFromCoordinates } from "context/PlotMap/geo";
import {
  MAP_PREFS_STORAGE_KEY,
  SECTOR_PREF_KEY,
  TAB_OPEN_PREF_KEY,
  defaultMapPrefs,
} from "defs";

interface LocationMarkerProperties {
  label: string;
  lat: number;
  lng: number;
  location: string;
}

interface LocationMarkersProps {}

const defaultGrowers: GrowerGeoResponse[] = [];
const defaultLocations: LocationResponse[] = [];

const LOCATION_MARKERS_CLUSTER_COUNT_LAYER_ID =
  "location-markers-cluster-count";
const LOCATION_MARKERS_CLUSTER_LAYER_ID = "location-markers-cluster";
const LOCATION_MARKERS_LABEL_LAYER_ID = "location-markers-label";
const LOCATION_MARKERS_PIN_LAYER_ID = "location-markers-pin";
const LOCATION_MARKERS_SOURCE_ID = "location-markers";

const CLUSTER_FILTER = ["has", "point_count"];
const UNCLUSTERED_FILTER = ["!", ["has", "point_count"]];

const getLocationMarkerPoint = (
  growers: GrowerGeoResponse[],
  location: LocationResponse,
): { latitude: number; longitude: number } | null => {
  const meanCenter = getMeanBoundaryCenter(
    growers.map((grower) => grower.boundary),
  );
  if (meanCenter != null) {
    return meanCenter;
  }
  if (location.latitude == null || location.longitude == null) {
    return null;
  }
  return {
    latitude: location.latitude,
    longitude: location.longitude,
  };
};

const LocationMarkers: React.FC<LocationMarkersProps> = () => {
  const { default: map } = useMap();
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();
  const [storedMapPrefs] = useLocalStorage(
    MAP_PREFS_STORAGE_KEY,
    defaultMapPrefs,
  );
  const zoomToSector = storedMapPrefs[SECTOR_PREF_KEY];
  const sectorBounds =
    zoomToSector == null ? undefined : SECTOR_BOUNDS[zoomToSector];
  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const showMarkers = locationUuid === "";

  const { data: growers = defaultGrowers } = useGetGrowersGeoByLocation(
    undefined,
    {
      query: {
        enabled: showMarkers,
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : defaultGrowers,
      },
    },
  );
  const { data: locations = defaultLocations } = useGetAllLocations(undefined, {
    query: {
      enabled: showMarkers,
      select: (res): LocationResponse[] =>
        res.status === 200 ? res.data : defaultLocations,
    },
  });

  const growersByLocationUuid = new Map<string, GrowerGeoResponse[]>();
  growers.forEach((grower) => {
    const existing = growersByLocationUuid.get(grower.location_uuid);
    if (existing == null) {
      growersByLocationUuid.set(grower.location_uuid, [grower]);
      return;
    }
    existing.push(grower);
  });

  const locationsWithGps = locations.flatMap((location) => {
    const point = getLocationMarkerPoint(
      growersByLocationUuid.get(location.location_uuid) ?? defaultGrowers,
      location,
    );
    if (point == null) {
      return [];
    }
    return [{ ...point, location }];
  });

  const markerVisibility = showMarkers ? "visible" : "none";
  const markersGeoJson = showMarkers
    ? {
        type: "FeatureCollection" as const,
        features: locationsWithGps.map(({ latitude, location, longitude }) => ({
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [longitude, latitude],
          },
          properties: {
            label: location.location_name ?? location.location_uuid,
            lat: latitude,
            lng: longitude,
            location: location.location_uuid,
          } satisfies LocationMarkerProperties,
        })),
      }
    : EMPTY_FEATURE_COLLECTION;

  // Always load the pin image on the map, even if the markers are not shown. The image
  // isn't that big and it prevents race conditions with react-map-gl events.
  useEffect(() => {
    if (map == null) {
      return;
    }

    const mapboxMap = map.getMap();
    const handleAddPinImage = () => {
      void addLocationPinImage(mapboxMap);
    };

    if (mapboxMap.isStyleLoaded()) {
      handleAddPinImage();
    } else {
      mapboxMap.once("idle", handleAddPinImage);
    }

    return () => {
      mapboxMap.off("idle", handleAddPinImage);
    };
  }, [map]);

  useEffect(() => {
    if (!isReady || !showMarkers || map == null) {
      return;
    }

    const mapboxMap = map.getMap();
    const clusterLayerIds = [
      LOCATION_MARKERS_CLUSTER_LAYER_ID,
      LOCATION_MARKERS_CLUSTER_COUNT_LAYER_ID,
    ];
    const markerLayerIds = [
      LOCATION_MARKERS_LABEL_LAYER_ID,
      LOCATION_MARKERS_PIN_LAYER_ID,
    ];
    let isCancelled = false;

    const handleClusterClick = (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      const clusterId = (
        feature?.properties as { cluster_id?: unknown } | null | undefined
      )?.cluster_id;
      if (typeof clusterId !== "number" || feature?.geometry.type !== "Point") {
        return;
      }

      const [lng, lat] = feature.geometry.coordinates;
      const source = mapboxMap.getSource(LOCATION_MARKERS_SOURCE_ID);
      if (
        lng == null ||
        lat == null ||
        source == null ||
        source.type !== "geojson"
      ) {
        return;
      }

      source.getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (isCancelled || error != null || zoom == null) {
          return;
        }
        mapboxMap.easeTo({
          center: [lng, lat],
          duration: FIT_BOUNDS_OPTIONS.duration,
          zoom,
        });
      });
    };

    const handleMarkerClick = (event: MapMouseEvent) => {
      const properties = event.features?.[0]?.properties as
        LocationMarkerProperties | null | undefined;
      if (properties == null) {
        return;
      }

      setSearchParams(
        (prev) => {
          let next = updateParamValue(
            prev,
            "lat",
            prefs,
            String(properties.lat),
          );
          next = updateParamValue(next, "lng", prefs, String(properties.lng));
          next = updateParamValue(next, "location", prefs, properties.location);
          next = updateParamValue(next, TAB_OPEN_PREF_KEY, prefs, "Y");
          return next;
        },
        { replace: true, state: { inPageNav: true } },
      );
    };

    const handleMarkerEnter = () => {
      mapboxMap.getCanvas().style.cursor = "pointer";
    };

    const handleMarkerLeave = () => {
      mapboxMap.getCanvas().style.cursor = "";
    };

    clusterLayerIds.forEach((layerId) => {
      mapboxMap.on("click", layerId, handleClusterClick);
      mapboxMap.on("mouseenter", layerId, handleMarkerEnter);
      mapboxMap.on("mouseleave", layerId, handleMarkerLeave);
    });
    markerLayerIds.forEach((layerId) => {
      mapboxMap.on("click", layerId, handleMarkerClick);
      mapboxMap.on("mouseenter", layerId, handleMarkerEnter);
      mapboxMap.on("mouseleave", layerId, handleMarkerLeave);
    });

    return () => {
      isCancelled = true;
      clusterLayerIds.forEach((layerId) => {
        mapboxMap.off("click", layerId, handleClusterClick);
        mapboxMap.off("mouseenter", layerId, handleMarkerEnter);
        mapboxMap.off("mouseleave", layerId, handleMarkerLeave);
      });
      markerLayerIds.forEach((layerId) => {
        mapboxMap.off("click", layerId, handleMarkerClick);
        mapboxMap.off("mouseenter", layerId, handleMarkerEnter);
        mapboxMap.off("mouseleave", layerId, handleMarkerLeave);
      });
    };
  }, [isReady, map, prefs, setSearchParams, showMarkers]);

  useEffect(() => {
    if (!showMarkers || map == null || locationsWithGps.length === 0) {
      return;
    }

    const bounds = sectorBounds ?? getBoundsFromCoordinates(locationsWithGps);
    if (bounds == null) {
      return;
    }

    map.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
  }, [locationsWithGps, map, sectorBounds, showMarkers]);

  return (
    <Source
      cluster
      clusterMaxZoom={LOCATION_CLUSTER_MAX_ZOOM}
      clusterRadius={LOCATION_CLUSTER_RADIUS}
      data={markersGeoJson}
      id={LOCATION_MARKERS_SOURCE_ID}
      type="geojson"
    >
      <Layer
        filter={CLUSTER_FILTER}
        id={LOCATION_MARKERS_CLUSTER_LAYER_ID}
        layout={{ visibility: markerVisibility }}
        paint={{
          "circle-color": theme.palette.info.main,
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["get", "point_count"],
            2,
            16,
            50,
            24,
          ],
          "circle-stroke-color": theme.palette.common.white,
          "circle-stroke-width": 2,
        }}
        type="circle"
      />
      <Layer
        filter={CLUSTER_FILTER}
        id={LOCATION_MARKERS_CLUSTER_COUNT_LAYER_ID}
        layout={{
          "text-allow-overlap": true,
          "text-field": ["get", "point_count_abbreviated"],
          "text-ignore-placement": true,
          "text-size": 14,
          visibility: markerVisibility,
        }}
        paint={{
          "text-color": LABEL_FILL_COLOR,
        }}
        type="symbol"
      />
      <Layer
        filter={UNCLUSTERED_FILTER}
        id={LOCATION_MARKERS_PIN_LAYER_ID}
        layout={{
          "icon-allow-overlap": true,
          "icon-anchor": "bottom",
          "icon-ignore-placement": true,
          "icon-image": LOCATION_PIN_IMAGE_ID,
          visibility: markerVisibility,
        }}
        type="symbol"
      />
      <Layer
        filter={UNCLUSTERED_FILTER}
        id={LOCATION_MARKERS_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconAnchor: "top",
            iconImage: LABEL_PLAQUE_IMAGE_IDS.location,
          }),
          "icon-allow-overlap": false,
          "icon-ignore-placement": false,
          "text-allow-overlap": false,
          "text-anchor": "top",
          "text-field": ["get", "label"],
          "text-ignore-placement": false,
          "text-padding": 0,
          "text-size": 14,
          visibility: markerVisibility,
        }}
        paint={{
          "icon-translate": [0, 10],
          "icon-translate-anchor": "viewport",
          "text-color": LABEL_FILL_COLOR,
          "text-translate": [0, 10],
          "text-translate-anchor": "viewport",
        }}
        type="symbol"
      />
    </Source>
  );
};

export default LocationMarkers;
